import {
  type EventCatalogEntry,
  findEventEntry,
} from "@repo/vihangam-yog-shared";

interface OctoberDay {
  slug: string;
  /** Organiser input; the UI hides the poster tile when this is null. */
  poster: string | null;
  /** What the poster shows, for people who cannot see it. */
  posterAlt?: string;
  whatToBring: string[];
  /** Practical lines people must not miss on the day (facilities, what
   *  to expect), shown in the highlight block on every surface. */
  notices: string[];
}

interface HawanDay extends OctoberDay {
  /** The organisers' four objectives, shown verbatim on the events page. */
  objectives: string[];
}

/** What a registrant agrees to; each entry is one short sentence. */
interface Policies {
  refunds: string[];
  privacy: string[];
}

interface VisitFlyer {
  src: string;
  alt: string;
}

/** A portrait of one of the two Masters visiting, with the caption under it. */
interface MasterPortrait {
  src: string;
  alt: string;
  name: string;
  /** "Sadguru" or "Sant Pravar": the line under the name. */
  title: string;
}

interface Landmark {
  src: string;
  alt: string;
  caption: string;
}

/** A programme in the visit that people attend only when invited. It is
 *  listed for completeness and takes no registration on this site. */
interface InvitationProgramme {
  dateLabel: string;
  title: string;
  /** Venue name with the level, as the organisers wrote it. */
  venue: string;
  address: string;
  timeLabel: string;
  /** A map search for the address, so the card reads like the open days. */
  mapUrl: string;
}

interface OctoberContent {
  stage: OctoberDay;
  hawan: HawanDay;
  /** Organiser input; the UI hides the WhatsApp link when this is null. The
   *  consent checkbox on the form shows regardless. */
  whatsappGroupUrl: string | null;
  /** Refund policy and data-protection wording, shown on the form. */
  policies: Policies;
  /** The organisers' one-page flyer for the whole 2026 visit. */
  visitFlyer: VisitFlyer;
  /** Swamiji and Sarkar, shown at the top of the register and events pages. */
  masters: readonly MasterPortrait[];
  /** Swarved Mahamandir, shown at the end of those pages. */
  mahamandir: Landmark;
  /** Facts about the visit that sit outside the two catalog days. */
  visit: {
    datesLabel: string;
    otherProgrammes: string;
    /** The one line that frames everything: who is coming and why there
     *  are programmes. Shown at the top of the home and events pages. */
    occasion: string;
  };
  /** The invitation-only dialogues before the two open days. Shown on the
   *  events page without a Register button. */
  invitationOnly: readonly InvitationProgramme[];
}

/** Facts the organiser still owes are null here; the UI hides what is null. */
export const OCTOBER: OctoberContent = {
  stage: {
    slug: "sg-stage-programme-oct-03",
    poster: null,
    whatToBring: ["Come as you are. Seating is provided."],
    notices: [],
  },
  hawan: {
    slug: "sg-hawan-yajna-oct-04",
    // A real Hawan kund with the fire lit, from the organisers (10 Sep 2026).
    poster: "/images/visit/hawan-kund.jpg",
    posterAlt:
      "A square metal Hawan kund with the fire lit and a hand offering grains",
    whatToBring: [
      "Wear comfortable clothing. Prasad is served after the Yajna.",
    ],
    // Verbatim from the organisers (10 Sep 2026).
    notices: [
      "Only limited portable toilets will be available. Please come prepared.",
    ],
    // Verbatim from the organisers (restated 10 Sep 2026).
    objectives: [
      "World Peace",
      "Peace for our Ancestors",
      "Wish fulfilment",
      "Continued Harmony among people of Singapore for their economic, mental and spiritual upliftment",
    ],
  },
  whatsappGroupUrl: null,
  // cut-off is one week before the Yajna; the retention period is a
  // proposal. Edit the sentences here; the form renders them as given.
  policies: {
    refunds: [
      "Hawan Yajna contributions are not refundable.",
      "If you cannot come on the day, the Hawan is still performed in your name.",
    ],
    privacy: [
      "Vihangam Yoga Singapore Ltd collects the details on this form to run these two events, count food and seating, and contact you about them, under Singapore's Personal Data Protection Act.",
      "We do not share your details with anyone outside the organising team, and we delete registration data within 90 days of the event.",
    ],
  },
  visitFlyer: {
    src: "/events/swamiji-visit-2026.jpg",
    alt: "Swamiji's overseas visit 2026: Singapore programmes on 3 and 4 October",
  },
  // Photos from the organisers, 10 Sep 2026.
  masters: [
    {
      src: "/images/visit/sadguru-swatantra-deo-ji.jpg",
      alt: "Sadguru Swatantra Deo Ji Maharaj in meditation",
      name: "Sadguru Swatantra Deo Ji Maharaj",
      title: "Swamiji",
    },
    {
      src: "/images/visit/sant-shri-naamdeo-ji.png",
      alt: "Sant Shri Naam Deo Ji Maharaj seated in meditation",
      name: "Sant Shri Naam Deo Ji Maharaj",
      title: "Sarkar",
    },
  ],
  mahamandir: {
    src: "/images/visit/swarved-mahamandir.jpg",
    alt: "Swarved Mahamandir in Varanasi, crowds gathered before the temple at sunset",
    caption:
      "Swarved Mahamandir, Varanasi. The largest meditation centre in the world, with room for 20,000.",
  },
  visit: {
    datesLabel: "Swamiji is in Singapore from 28 September to 6 October 2026.",
    otherProgrammes:
      "Smaller programmes are also scheduled on 2, 4 and 5 October.",
    occasion:
      "On the occasion of the visit of Sadguru Swatantra Deo Ji Maharaj and Sant Shri Naam Deo Ji Maharaj, four programmes are organised in Singapore. Two are by invitation. Two are open to everyone, with one registration for both.",
  },
  // Verbatim from the organisers (12 Sep 2026). Both are by invitation only.
  invitationOnly: [
    {
      dateLabel: "Thursday 1 October 2026",
      title: "Interfaith Spiritual Dialogue",
      venue: "L3, Shree Laxmi Narayan Temple",
      address: "10 Chander Road",
      timeLabel: "6:30 pm to 9:00 pm",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Shree+Laxmi+Narayan+Temple+10+Chander+Road+Singapore",
    },
    {
      dateLabel: "Friday 2 October 2026",
      title: "Spiritual Dialogue with Business Leaders",
      venue: "L3, Arya Samaj",
      address: "113 Syed Alwi Road",
      timeLabel: "6:30 pm to 9:00 pm",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=Arya+Samaj+113+Syed+Alwi+Road+Singapore",
    },
  ],
};

export function octoberEvents(): [
  EventCatalogEntry | undefined,
  EventCatalogEntry | undefined,
] {
  return [
    findEventEntry(OCTOBER.stage.slug),
    findEventEntry(OCTOBER.hawan.slug),
  ];
}
